import { Col, Flex, InputNumber, Row, Space } from "antd";
import TextArea from "antd/es/input/TextArea";
import { t } from "i18next";
import React, { useState } from "react";

import { Input } from "../../../../../components/Input/Input";
import { MandatoryField } from "../../../../../components/MandatoryField/MandatoryField";
import { Select } from "../../../../../components/Select/Select";
import { exciseLicencesService } from "../../../../../modules/obligee/exciseLicences/exciseLicences.service";
import {
  IMereVelicineProps,
  ISkladisteInformacijeProps,
} from "../../../../../modules/obligee/exciseLicences/exciseLicences.types";
import { AkciznaMarkicaKategorijaProzivoda } from "../../../../../modules/obligee/exciseStamps/exciseStamps.types";
import { clearNonNumericValues } from "../../../../../utils/functions";
import style from "./CenterPart.module.scss";

interface ICenterPartProps {
  meraSelect: IMereVelicineProps[];
  selectedSkladisteId: number | null;
  stateValues: ISkladisteInformacijeProps;
  licenceRequest: number | null;
  data: ISkladisteInformacijeProps[];
  setExciseLicences: React.Dispatch<React.SetStateAction<any>>;
  setInformacijeSkladista: (
    updatedState: Partial<ISkladisteInformacijeProps>
  ) => void;
  meraVelicinaSkladistaError: string | null;
  kategorijaProizvodaId: AkciznaMarkicaKategorijaProzivoda | null;
}

export const CenterPart: React.FC<ICenterPartProps> = ({
  meraSelect,
  data,
  stateValues,
  licenceRequest,
  setInformacijeSkladista,
  meraVelicinaSkladistaError,
  kategorijaProizvodaId,
}) => {
  let queryParams = new URLSearchParams(window.location.search);
  const id = queryParams.get("id");

  const options = exciseLicencesService.getSkladistaOptions(data);
  const meraOptions = exciseLicencesService.getMeraOptions(meraSelect);
  const [selectValue, setSelectValue] = useState<number | null>(Number(id));

  const getPreSelectedOption = () => {
    if (id) {
      // setSelectValue(Number(id));
      const entitet = data.find(
        (skladiste: ISkladisteInformacijeProps) =>
          skladiste.skladisteId === Number(id)
      );
      setInformacijeSkladista({
        kolicinaProizvodaIzPrethodneGodine:
          entitet?.kolicinaProizvodaIzPrethodneGodine,
        planiranaProizvodnjaProizvoda: entitet?.planiranaProizvodnjaProizvoda,
        brojSkladistaZaDozvolu: entitet?.brojSkladistaZaDozvolu,
        ulicaSkladista: entitet?.ulicaSkladista,
        brojUliceSkladista: entitet?.brojUliceSkladista,
        postanskiBrojSkladista: entitet?.postanskiBrojSkladista,
        mestoSkladista: entitet?.mestoSkladista,
        oznakaSkladista: entitet?.oznakaSkladista,
        meraVelicineSkladista: entitet?.meraVelicineSkladista,
        meraVrednost: entitet?.meraVrednost,
        opisSkladista: entitet?.opisSkladista,
        opisRadaRacunovodstva: entitet?.opisRadaRacunovodstva,
      });
    }
    return null;
  };

  const isDisabled = () => {
    if (licenceRequest === 2 || licenceRequest === 3) return false;
    if (id) return true;
  };

  const isMandatoryIfDisabled = (label: string) => {
    if (isDisabled() === false) return MandatoryField(label);
    if (!id) return MandatoryField(label);
    return t(label);
  };

  return (
    <Col span={24} className={style.mainContainer}>
      <Row>
        <Col span={12}>
          {id ? (
            <Row className={`${style.leftCol} pb-30`}>
              {options && (
                <>
                  <Col span={11} className="alignRight pt-6 pr-7">
                    <span className={style.fontSize}>
                      {t("AKCIZNE_DOZVOLE.SKLADISTE")}:
                    </span>
                  </Col>
                  <Col span={12}>
                    <Select
                      className={`${style.fontSize} w-90`}
                      allowClear={!id}
                      disabled={!!id}
                      value={selectValue}
                      // onChange={handleOnChange}
                      options={options || []}
                      name="izbor-skladista"
                    />
                  </Col>
                </>
              )}
            </Row>
          ) : (
            <div className={style.title}>
              {t("AKCIZNE_DOZVOLE.INFORMACIJE_O_SKLADISTU")}
            </div>
          )}
          {kategorijaProizvodaId === AkciznaMarkicaKategorijaProzivoda.DUVAN ? (
            <Row className={style.leftCol}>
              <Col span={11} className="alignRight pr-7 pb-15">
                <span className={style.fontSize}>
                  {isMandatoryIfDisabled(
                    "AKCIZNE_DOZVOLE.OSTVARENA_KOLICINA_PROIZVEDENIH_AKCIZNIH_PROIZVODA_U_PROTEKLOJ_KALENDARSKOJ_GODINI"
                  )}
                  :
                </span>
              </Col>
              <Col span={12}>
                <Space.Compact style={{ width: "100%" }}>
                  <InputNumber
                    onChange={(value) =>
                      setInformacijeSkladista({
                        kolicinaProizvodaIzPrethodneGodineKg: value,
                      })
                    }
                    min={0}
                    placeholder={"kg"}
                    className={`${style.fontSize} w-40`}
                    disabled={isDisabled()}
                    step={0.1}
                    value={stateValues.kolicinaProizvodaIzPrethodneGodineKg}
                    name="kolicina-proizvoda-prethodne-godine-kg"
                    onKeyDown={(event) => clearNonNumericValues(event, ["."])}
                  />
                  <InputNumber
                    min={0}
                    disabled={isDisabled()}
                    className={`${style.fontSize} w-60`}
                    placeholder={t("PLACEHOLDERS.KOMADI")}
                    onChange={(value) =>
                      setInformacijeSkladista({
                        kolicinaProizvodaIzPrethodneGodineKomadi: value,
                      })
                    }
                    value={stateValues.kolicinaProizvodaIzPrethodneGodineKomadi}
                    name="kolicina-proizvoda-prethodne-godine-komadi"
                    onKeyDown={clearNonNumericValues}
                  />
                </Space.Compact>
              </Col>
              <Col span={11} className="alignRight pr-7">
                <span className={style.fontSize}>
                  {isMandatoryIfDisabled(
                    "AKCIZNE_DOZVOLE.PLANIRANE_KOLICINE_GODISNJE_PROIZVODNJE_AKCIZNOG_PROIZVODA"
                  )}
                  :
                </span>
              </Col>
              <Col span={12}>
                <Space.Compact style={{ width: "100%" }}>
                  <InputNumber
                    onChange={(value) =>
                      setInformacijeSkladista({
                        planiranaProizvodnjaProizvodaKg: value,
                      })
                    }
                    min={0}
                    placeholder={"kg"}
                    className={`${style.fontSize} w-40`}
                    disabled={isDisabled()}
                    step={0.1}
                    value={stateValues.planiranaProizvodnjaProizvodaKg}
                    name="planirane-kolicine-godisnje-proizvodnje-kg"
                    onKeyDown={(event) => clearNonNumericValues(event, ["."])}
                  />
                  <InputNumber
                    min={0}
                    disabled={isDisabled()}
                    className={`${style.fontSize} w-60`}
                    placeholder={t("PLACEHOLDERS.KOMADI")}
                    onChange={(value) =>
                      setInformacijeSkladista({
                        planiranaProizvodnjaProizvodaKomadi: value,
                      })
                    }
                    value={stateValues.planiranaProizvodnjaProizvodaKomadi}
                    name="planirane-kolicine-godisnje-proizvodnje-komadi"
                    onKeyDown={clearNonNumericValues}
                  />
                </Space.Compact>
              </Col>
            </Row>
          ) : (
            <Row className={style.leftCol}>
              <Col span={11} className="alignRight pr-7 pb-15">
                <span className={style.fontSize}>
                  {isMandatoryIfDisabled(
                    "AKCIZNE_DOZVOLE.OSTVARENA_KOLICINA_PROIZVEDENIH_AKCIZNIH_PROIZVODA_U_PROTEKLOJ_KALENDARSKOJ_GODINI"
                  )}
                  :
                </span>
              </Col>
              <Col span={12}>
                <InputNumber
                  min={0}
                  onChange={(value) =>
                    setInformacijeSkladista({
                      kolicinaProizvodaIzPrethodneGodine: value,
                    })
                  }
                  value={stateValues.kolicinaProizvodaIzPrethodneGodine}
                  disabled={isDisabled()}
                  className={`${style.fontSize} w-100`}
                  name="kolicina-akciznih-proizvoda-proizvedenih"
                  onKeyDown={clearNonNumericValues}
                />
              </Col>
              <Col span={11} className="alignRight pr-7">
                <span className={style.fontSize}>
                  {isMandatoryIfDisabled(
                    "AKCIZNE_DOZVOLE.PLANIRANE_KOLICINE_GODISNJE_PROIZVODNJE_AKCIZNOG_PROIZVODA"
                  )}
                  :
                </span>
              </Col>
              <Col span={12}>
                <InputNumber
                  min={0}
                  onChange={(value) =>
                    setInformacijeSkladista({
                      planiranaProizvodnjaProizvoda: value,
                    })
                  }
                  className={`${style.fontSize} w-100`}
                  value={stateValues.planiranaProizvodnjaProizvoda}
                  disabled={isDisabled()}
                  name="planirane-kolicine-godisnje-proizvodnje"
                  onKeyDown={clearNonNumericValues}
                />
              </Col>
            </Row>
          )}
          <Row className={style.leftCol}>
            <Col span={11} className="alignRight pr-7">
              <span className={style.fontSize}>
                {isMandatoryIfDisabled(
                  "AKCIZNE_DOZVOLE.BR_SKLADISTA_ZA_KOJE_TRAZI_AKCIZNU_DOZVOLU"
                )}
                :
              </span>
            </Col>
            <Col span={12}>
              <Input
                onChange={(e) =>
                  setInformacijeSkladista({
                    brojSkladistaZaDozvolu: e.target.value,
                  })
                }
                className={`${style.fontSize} w-100`}
                value={stateValues.brojSkladistaZaDozvolu}
                disabled={isDisabled()}
                name="br-skladista-za-koje-trazi-akciznu-dozvolu"
              />
            </Col>
          </Row>
          <Row className={style.leftCol}>
            <Col span={11} className="alignRight pt-6 pr-7">
              <span className={style.fontSize}>
                {isMandatoryIfDisabled("AKCIZNE_DOZVOLE.ULICA_I_BROJ")}:
              </span>
            </Col>
            <Col span={12}>
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  onChange={(e) =>
                    setInformacijeSkladista({
                      ulicaSkladista: e.target.value,
                    })
                  }
                  placeholder={t("PLACEHOLDERS.ULICA")}
                  value={stateValues.ulicaSkladista}
                  disabled={isDisabled()}
                  className={`${style.fontSize} w-75`}
                  name="ulica"
                />
                <Input
                  onChange={(e) =>
                    setInformacijeSkladista({
                      brojUliceSkladista: e.target.value,
                    })
                  }
                  placeholder={t("PLACEHOLDERS.BROJ")}
                  value={stateValues.brojUliceSkladista}
                  disabled={isDisabled()}
                  className={`${style.fontSize} w-25`}
                  name="broj"
                />
              </Space.Compact>
            </Col>
          </Row>
          <Row className={style.leftCol}>
            <Col span={11} className="alignRight pt-6 pr-7">
              <span className={style.fontSize}>
                {isMandatoryIfDisabled("AKCIZNE_DOZVOLE.POSTANSKI_BROJ")}:
              </span>
            </Col>
            <Col span={12}>
              <Input
                onChange={(e) =>
                  setInformacijeSkladista({
                    postanskiBrojSkladista: e.target.value,
                  })
                }
                className={`${style.fontSize} w-100`}
                value={stateValues.postanskiBrojSkladista}
                disabled={isDisabled()}
                name="postanski-broj"
              />
            </Col>
          </Row>
          <Row className={style.leftCol}>
            <Col span={11} className="alignRight pt-6 pr-7">
              <span className={style.fontSize}>
                {isMandatoryIfDisabled("AKCIZNE_DOZVOLE.MESTO_SKLADISTA")}:
              </span>
            </Col>
            <Col span={12}>
              <Input
                onChange={(e) =>
                  setInformacijeSkladista({
                    mestoSkladista: e.target.value,
                  })
                }
                className={`${style.fontSize} w-100`}
                value={stateValues.mestoSkladista}
                disabled={isDisabled()}
                name="mesto-skladista"
              />
            </Col>
          </Row>
          <Row className={style.leftCol}>
            <Col span={11} className="alignRight pt-6 pr-7">
              <span className={style.fontSize}>
                {isMandatoryIfDisabled("AKCIZNE_DOZVOLE.OZNAKA_SKLADISTA")}:
              </span>
            </Col>
            <Col span={12}>
              <Input
                onChange={(e) =>
                  setInformacijeSkladista({
                    oznakaSkladista: e.target.value,
                  })
                }
                value={stateValues.oznakaSkladista}
                disabled={isDisabled()}
                className={`${style.fontSize} w-100`}
                name="oznaka-skladista"
              />
            </Col>
          </Row>
          <Row className={style.leftCol}>
            <Col span={11} className="alignRight pt-6 pr-7">
              <span className={style.fontSize}>
                {isMandatoryIfDisabled("PLACEHOLDERS.POVRSINA_ZAPREMINA")}:
              </span>
            </Col>
            <Col span={12}>
              <Flex
                align="center"
                gap={4}
                className={`${style.fontSize} w-100`}
              >
                <Select
                  name="mera"
                  disabled={isDisabled()}
                  onChange={(e) =>
                    setInformacijeSkladista({ meraVelicineSkladista: e })
                  }
                  value={stateValues.meraVelicineSkladista}
                  options={meraOptions || []}
                  className={style.fontSize}
                  style={{ width: "80%" }}
                  placeholder={t("PLACEHOLDERS.MERA")}
                  allowClear
                />
                <InputNumber
                  min={0}
                  value={stateValues.meraVrednost}
                  onChange={(value) =>
                    setInformacijeSkladista({
                      meraVrednost: value,
                    })
                  }
                  placeholder={t("AKCIZNE_DOZVOLE.VREDNOST")}
                  className={style.fontSize}
                  style={{
                    width: "100%",
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                  }}
                  disabled={isDisabled()}
                  name="vrednost-povrsine"
                />
              </Flex>
              {meraVelicinaSkladistaError ? (
                <div style={{ color: "red", marginTop: 4 }}>
                  {meraVelicinaSkladistaError}
                </div>
              ) : (
                <></>
              )}
            </Col>
          </Row>
        </Col>
        <Col span={12} className="pl-25 pt-60">
          <div className="pb-15">
            <span className={style.fontSize}>
              {isMandatoryIfDisabled("AKCIZNE_DOZVOLE.OPIS_AKCIZNOG_SKLADISTA")}
              :
            </span>
          </div>
          <TextArea
            onChange={(e) =>
              setInformacijeSkladista({
                opisSkladista: e.target.value,
              })
            }
            value={stateValues.opisSkladista}
            disabled={isDisabled()}
            maxLength={240}
            className={`${style.fontSize} mb-15`}
            rows={4}
          />
          <div className="pb-15">
            <span className={style.fontSize}>
              {isMandatoryIfDisabled(
                "AKCIZNE_DOZVOLE.OPIS_RADA_SISTEMA_RACUNOVODSTVA"
              )}
              :
            </span>
          </div>
          <TextArea
            onChange={(e) =>
              setInformacijeSkladista({
                opisRadaRacunovodstva: e.target.value,
              })
            }
            value={stateValues.opisRadaRacunovodstva}
            disabled={isDisabled()}
            maxLength={240}
            className={style.fontSize}
            rows={4}
          />
        </Col>
      </Row>
    </Col>
  );
};
